import Papa from 'papaparse';
import * as Yup from 'yup';
import { ScenarioPackCsvHeaders, ScenarioCsvHeaders } from 'src/api/types/misc';
import {
  ScenarioPackImportPayload,
  ScenarioPackImportScenarioInput,
} from 'src/types/contentImport';
import { Variable } from 'src/api/types';
import {
  convertDateToISO,
  convertToNumber,
  convertToString,
  delimiters,
  isValidBooleanLikeValue,
  isValidIntegerValue,
  isValidNumberValue,
  normalizeCsvHeader,
  normalizeCsvRowValues,
  ParseResult,
  ValidationErrorItem,
  validateRow,
} from 'src/utils/csv';
import { parseConsequenceValue } from 'src/utils/formula';
import {
  mapConsequenceToNumber,
  mapEventToNumber,
  mapSourceToNumber,
} from 'src/components/modals/AddRiskScenarioModal/forms/FromCsv/util';

export interface ScenarioPackCsvRow {
  [ScenarioPackCsvHeaders.PackID]?: string | null;
  [ScenarioPackCsvHeaders.PackName]?: string | null;
  [ScenarioPackCsvHeaders.PackDescription]?: string | null;
  [ScenarioPackCsvHeaders.PackAuthor]?: string | null;
  [ScenarioPackCsvHeaders.PackAuthorLogoPath]?: string | null;
  [ScenarioPackCsvHeaders.PackCategory]?: string | null;
  [ScenarioPackCsvHeaders.PackIsSystem]?: string | null;
  [ScenarioCsvHeaders.Name]?: string | null;
  [ScenarioPackCsvHeaders.ScenarioDescription]?: string | null;
  [ScenarioCsvHeaders.ID]?: string | null;
  [ScenarioCsvHeaders.Status]?: string | null;
  [ScenarioCsvHeaders.Sources]?: string | null;
  [ScenarioCsvHeaders.Events]?: string | null;
  [ScenarioCsvHeaders.Consequences]?: string | null;
  [ScenarioCsvHeaders.RiskOwner]?: string | null;
  [ScenarioCsvHeaders.RiskNarrative]?: string | null;
  [ScenarioCsvHeaders.UnmodifiedFrequencyTimes]?: string | number | null;
  [ScenarioCsvHeaders.UnmodifiedFrequencyYears]?: string | number | null;
  [ScenarioCsvHeaders.FrequencyNote]?: string | null;
  [ScenarioCsvHeaders.UnmodifiedConsequenceLower]?: string | number | null;
  [ScenarioCsvHeaders.UnmodifiedConsequenceUpper]?: string | number | null;
  [ScenarioCsvHeaders.ConsequenceLowerFormula]?: string | null;
  [ScenarioCsvHeaders.ConsequenceUpperFormula]?: string | null;
  [ScenarioCsvHeaders.ConsequenceNote]?: string | null;
  [ScenarioCsvHeaders.IdentifiedDate]?: string | null;
  [ScenarioCsvHeaders.ReviewDate]?: string | null;
  [ScenarioCsvHeaders.RiskManagementStrategy]?: string | null;
  [ScenarioCsvHeaders.RiskManagementNote]?: string | null;
}

const scenarioPackPathNames: Record<string, string> = {
  [ScenarioPackCsvHeaders.PackID]: ScenarioPackCsvHeaders.PackID,
  [ScenarioPackCsvHeaders.PackName]: ScenarioPackCsvHeaders.PackName,
  [ScenarioPackCsvHeaders.PackDescription]: ScenarioPackCsvHeaders.PackDescription,
  [ScenarioPackCsvHeaders.PackAuthor]: ScenarioPackCsvHeaders.PackAuthor,
  [ScenarioPackCsvHeaders.PackAuthorLogoPath]: ScenarioPackCsvHeaders.PackAuthorLogoPath,
  [ScenarioPackCsvHeaders.PackCategory]: ScenarioPackCsvHeaders.PackCategory,
  [ScenarioPackCsvHeaders.PackIsSystem]: ScenarioPackCsvHeaders.PackIsSystem,
  [ScenarioCsvHeaders.Name]: ScenarioCsvHeaders.Name,
  [ScenarioPackCsvHeaders.ScenarioDescription]: ScenarioPackCsvHeaders.ScenarioDescription,
  [ScenarioCsvHeaders.ID]: ScenarioCsvHeaders.ID,
  [ScenarioCsvHeaders.Status]: ScenarioCsvHeaders.Status,
  [ScenarioCsvHeaders.Sources]: ScenarioCsvHeaders.Sources,
  [ScenarioCsvHeaders.Events]: ScenarioCsvHeaders.Events,
  [ScenarioCsvHeaders.Consequences]: ScenarioCsvHeaders.Consequences,
  [ScenarioCsvHeaders.RiskOwner]: ScenarioCsvHeaders.RiskOwner,
  [ScenarioCsvHeaders.RiskNarrative]: ScenarioCsvHeaders.RiskNarrative,
  [ScenarioCsvHeaders.UnmodifiedFrequencyTimes]: ScenarioCsvHeaders.UnmodifiedFrequencyTimes,
  [ScenarioCsvHeaders.UnmodifiedFrequencyYears]: ScenarioCsvHeaders.UnmodifiedFrequencyYears,
  [ScenarioCsvHeaders.FrequencyNote]: ScenarioCsvHeaders.FrequencyNote,
  [ScenarioCsvHeaders.UnmodifiedConsequenceLower]: ScenarioCsvHeaders.UnmodifiedConsequenceLower,
  [ScenarioCsvHeaders.UnmodifiedConsequenceUpper]: ScenarioCsvHeaders.UnmodifiedConsequenceUpper,
  [ScenarioCsvHeaders.ConsequenceLowerFormula]: ScenarioCsvHeaders.ConsequenceLowerFormula,
  [ScenarioCsvHeaders.ConsequenceUpperFormula]: ScenarioCsvHeaders.ConsequenceUpperFormula,
  [ScenarioCsvHeaders.ConsequenceNote]: ScenarioCsvHeaders.ConsequenceNote,
  [ScenarioCsvHeaders.IdentifiedDate]: ScenarioCsvHeaders.IdentifiedDate,
  [ScenarioCsvHeaders.ReviewDate]: ScenarioCsvHeaders.ReviewDate,
  [ScenarioCsvHeaders.RiskManagementStrategy]: ScenarioCsvHeaders.RiskManagementStrategy,
  [ScenarioCsvHeaders.RiskManagementNote]: ScenarioCsvHeaders.RiskManagementNote,
};

const maxCharsMessage = (label: string, max: number) => `${label} can be up to ${max} characters.`;
const integerMessage = (label: string) => `${label} must be a whole number.`;
const dateMessage = (label: string) => `${label} must be a valid date in DD-MM-YYYY format.`;

const scenarioRowHeaders = [
  ScenarioCsvHeaders.ID,
  ScenarioCsvHeaders.Name,
  ScenarioPackCsvHeaders.ScenarioDescription,
  ScenarioCsvHeaders.Status,
  ScenarioCsvHeaders.Sources,
  ScenarioCsvHeaders.Events,
  ScenarioCsvHeaders.Consequences,
  ScenarioCsvHeaders.RiskOwner,
  ScenarioCsvHeaders.RiskNarrative,
  ScenarioCsvHeaders.UnmodifiedFrequencyTimes,
  ScenarioCsvHeaders.UnmodifiedFrequencyYears,
  ScenarioCsvHeaders.FrequencyNote,
  ScenarioCsvHeaders.UnmodifiedConsequenceLower,
  ScenarioCsvHeaders.UnmodifiedConsequenceUpper,
  ScenarioCsvHeaders.ConsequenceLowerFormula,
  ScenarioCsvHeaders.ConsequenceUpperFormula,
  ScenarioCsvHeaders.ConsequenceNote,
  ScenarioCsvHeaders.IdentifiedDate,
  ScenarioCsvHeaders.ReviewDate,
  ScenarioCsvHeaders.RiskManagementStrategy,
  ScenarioCsvHeaders.RiskManagementNote,
] as const;

const hasTextValue = (value: unknown): boolean => value !== null && value !== undefined && String(value).trim() !== '';

const hasRawScenarioRowContent = (row: ScenarioPackCsvRow): boolean => {
  return scenarioRowHeaders.some((header) => hasTextValue(row[header]));
};

const isValidDateInput = (value: unknown): boolean => {
  if (!hasTextValue(value)) return true;
  return convertDateToISO(value) !== null;
};

const isValidConsequenceCellValue = (value: unknown): boolean => {
  if (!hasTextValue(value)) return true;
  const normalized = String(value).trim();
  if (normalized.includes('#')) return true;
  return isValidNumberValue(normalized);
};

const isValidFormulaInput = (value: unknown): boolean => {
  if (!hasTextValue(value)) return true;
  const normalized = String(value).trim();
  const sanitized = normalized.replace(/#[a-zA-Z][a-zA-Z0-9_]*/g, '1').replace(/#\d+/g, '1');
  return /^[\d+\-*/().%\s]+$/.test(sanitized);
};

const buildScenarioPackValidationSchema = (requirePackName: boolean) =>
  Yup.object({
    [ScenarioPackCsvHeaders.PackID]: Yup.string().nullable().max(255, maxCharsMessage(ScenarioPackCsvHeaders.PackID, 255)),
    [ScenarioPackCsvHeaders.PackName]: requirePackName
      ? Yup.string()
          .required(`${ScenarioPackCsvHeaders.PackName} is required.`)
          .max(255, maxCharsMessage(ScenarioPackCsvHeaders.PackName, 255))
      : Yup.string().nullable().max(255, maxCharsMessage(ScenarioPackCsvHeaders.PackName, 255)),
    [ScenarioPackCsvHeaders.PackDescription]: Yup.string().nullable(),
    [ScenarioPackCsvHeaders.PackAuthor]: Yup.string()
      .nullable()
      .max(255, maxCharsMessage(ScenarioPackCsvHeaders.PackAuthor, 255)),
    [ScenarioPackCsvHeaders.PackAuthorLogoPath]: Yup.string()
      .nullable()
      .max(255, maxCharsMessage(ScenarioPackCsvHeaders.PackAuthorLogoPath, 255)),
    [ScenarioPackCsvHeaders.PackCategory]: Yup.string()
      .nullable()
      .max(255, maxCharsMessage(ScenarioPackCsvHeaders.PackCategory, 255)),
    [ScenarioPackCsvHeaders.PackIsSystem]: Yup.string()
      .nullable()
      .test(
        'valid-pack-is-system',
        `${ScenarioPackCsvHeaders.PackIsSystem} must be Yes or No.`,
        (value) => isValidBooleanLikeValue(value),
      ),
    [ScenarioCsvHeaders.Name]: Yup.string()
      .nullable()
      .max(255, maxCharsMessage(ScenarioCsvHeaders.Name, 255))
      .test('scenario-name-required', `${ScenarioCsvHeaders.Name} is required for each scenario row.`, function (value) {
        const row = this.parent as ScenarioPackCsvRow;
        if (!hasRawScenarioRowContent(row)) return true;
        return hasTextValue(value);
      }),
    [ScenarioPackCsvHeaders.ScenarioDescription]: Yup.string().nullable(),
    [ScenarioCsvHeaders.ID]: Yup.string().nullable().max(255, maxCharsMessage(ScenarioCsvHeaders.ID, 255)),
    [ScenarioCsvHeaders.Status]: Yup.string().nullable().max(255, maxCharsMessage(ScenarioCsvHeaders.Status, 255)),
    [ScenarioCsvHeaders.Sources]: Yup.string().nullable(),
    [ScenarioCsvHeaders.Events]: Yup.string().nullable(),
    [ScenarioCsvHeaders.Consequences]: Yup.string().nullable(),
    [ScenarioCsvHeaders.RiskOwner]: Yup.string().nullable().max(255, maxCharsMessage(ScenarioCsvHeaders.RiskOwner, 255)),
    [ScenarioCsvHeaders.RiskNarrative]: Yup.string().nullable(),
    [ScenarioCsvHeaders.UnmodifiedFrequencyTimes]: Yup.mixed().test(
      'valid-frequency-times',
      integerMessage(ScenarioCsvHeaders.UnmodifiedFrequencyTimes),
      (value) => isValidIntegerValue(value),
    ),
    [ScenarioCsvHeaders.UnmodifiedFrequencyYears]: Yup.mixed().test(
      'valid-frequency-years',
      integerMessage(ScenarioCsvHeaders.UnmodifiedFrequencyYears),
      (value) => isValidIntegerValue(value),
    ),
    [ScenarioCsvHeaders.FrequencyNote]: Yup.string()
      .nullable()
      .max(2048, maxCharsMessage(ScenarioCsvHeaders.FrequencyNote, 2048)),
    [ScenarioCsvHeaders.UnmodifiedConsequenceLower]: Yup.mixed().test(
      'valid-consequence-lower',
      `${ScenarioCsvHeaders.UnmodifiedConsequenceLower} must be a number. Use the formula columns for formulas.`,
      (value) => isValidConsequenceCellValue(value),
    ),
    [ScenarioCsvHeaders.UnmodifiedConsequenceUpper]: Yup.mixed().test(
      'valid-consequence-upper',
      `${ScenarioCsvHeaders.UnmodifiedConsequenceUpper} must be a number. Use the formula columns for formulas.`,
      (value) => isValidConsequenceCellValue(value),
    ),
    [ScenarioCsvHeaders.ConsequenceLowerFormula]: Yup.string()
      .nullable()
      .max(255, maxCharsMessage(ScenarioCsvHeaders.ConsequenceLowerFormula, 255))
      .test(
        'valid-lower-formula',
        `${ScenarioCsvHeaders.ConsequenceLowerFormula} contains an invalid formula.`,
        (value) => isValidFormulaInput(value),
      ),
    [ScenarioCsvHeaders.ConsequenceUpperFormula]: Yup.string()
      .nullable()
      .max(255, maxCharsMessage(ScenarioCsvHeaders.ConsequenceUpperFormula, 255))
      .test(
        'valid-upper-formula',
        `${ScenarioCsvHeaders.ConsequenceUpperFormula} contains an invalid formula.`,
        (value) => isValidFormulaInput(value),
      ),
    [ScenarioCsvHeaders.ConsequenceNote]: Yup.string()
      .nullable()
      .max(2048, maxCharsMessage(ScenarioCsvHeaders.ConsequenceNote, 2048)),
    [ScenarioCsvHeaders.IdentifiedDate]: Yup.string()
      .nullable()
      .test('valid-identified-date', dateMessage(ScenarioCsvHeaders.IdentifiedDate), (value) => isValidDateInput(value)),
    [ScenarioCsvHeaders.ReviewDate]: Yup.string()
      .nullable()
      .test('valid-review-date', dateMessage(ScenarioCsvHeaders.ReviewDate), (value) => isValidDateInput(value)),
    [ScenarioCsvHeaders.RiskManagementStrategy]: Yup.string()
      .nullable()
      .max(255, maxCharsMessage(ScenarioCsvHeaders.RiskManagementStrategy, 255)),
    [ScenarioCsvHeaders.RiskManagementNote]: Yup.string()
      .nullable()
      .max(2048, maxCharsMessage(ScenarioCsvHeaders.RiskManagementNote, 2048)),
  });

const scenarioPackHeaders = [
  ScenarioPackCsvHeaders.PackID,
  ScenarioPackCsvHeaders.PackName,
  ScenarioPackCsvHeaders.PackDescription,
  ScenarioPackCsvHeaders.PackAuthor,
  ScenarioPackCsvHeaders.PackAuthorLogoPath,
  ScenarioPackCsvHeaders.PackCategory,
  ScenarioPackCsvHeaders.PackIsSystem,
  ScenarioCsvHeaders.Name,
  ScenarioPackCsvHeaders.ScenarioDescription,
  ScenarioCsvHeaders.ID,
  ScenarioCsvHeaders.Status,
  ScenarioCsvHeaders.Sources,
  ScenarioCsvHeaders.Events,
  ScenarioCsvHeaders.Consequences,
  ScenarioCsvHeaders.RiskOwner,
  ScenarioCsvHeaders.RiskNarrative,
  ScenarioCsvHeaders.UnmodifiedFrequencyTimes,
  ScenarioCsvHeaders.UnmodifiedFrequencyYears,
  ScenarioCsvHeaders.FrequencyNote,
  ScenarioCsvHeaders.UnmodifiedConsequenceLower,
  ScenarioCsvHeaders.UnmodifiedConsequenceUpper,
  ScenarioCsvHeaders.ConsequenceLowerFormula,
  ScenarioCsvHeaders.ConsequenceUpperFormula,
  ScenarioCsvHeaders.ConsequenceNote,
  ScenarioCsvHeaders.IdentifiedDate,
  ScenarioCsvHeaders.ReviewDate,
  ScenarioCsvHeaders.RiskManagementStrategy,
  ScenarioCsvHeaders.RiskManagementNote,
] as const;

const requiredScenarioPackHeaders = [
  ScenarioPackCsvHeaders.PackID,
  ScenarioPackCsvHeaders.PackName,
  ScenarioCsvHeaders.Name,
] as const;

const getMissingScenarioPackHeaders = (row: ScenarioPackCsvRow) => {
  const presentHeaders = new Set(Object.keys(row || {}).map(normalizeCsvHeader));
  return requiredScenarioPackHeaders.filter((header) => !presentHeaders.has(normalizeCsvHeader(header)));
};

const hasScenarioPackHeaders = (row: ScenarioPackCsvRow) => getMissingScenarioPackHeaders(row).length === 0;

const normalizeBoolean = (value: unknown): boolean | null => {
  if (value === null || value === undefined || String(value).trim() === '') {
    return null;
  }

  const normalized = String(value).trim().toLowerCase();
  if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
  if (['false', '0', 'no', 'n'].includes(normalized)) return false;
  return null;
};

const hasScenarioContent = (row: ScenarioPackImportScenarioInput) => {
  return Boolean(
    row.name ||
      row.description ||
      row.status ||
      row.riskOwner ||
      row.riskNarrative ||
      row.frequencyTimes !== null ||
      row.frequencyYears !== null ||
      row.frequencyNote ||
      row.lower !== null ||
      row.upper !== null ||
      row.lowerFormula ||
      row.upperFormula ||
      row.consequenceNote ||
      row.riskIdentifiedDate ||
      row.reviewDate ||
      row.managementStrategy ||
      row.managementNote ||
      row.source !== null ||
      row.event !== null ||
      row.consequence !== null,
  );
};

const createMetadataMismatchErrors = (rows: ScenarioPackCsvRow[]): ValidationErrorItem[] => {
  if (!rows.length) return [];

  const first = rows[0];
  const metadataHeaders = [
    ScenarioPackCsvHeaders.PackID,
    ScenarioPackCsvHeaders.PackName,
    ScenarioPackCsvHeaders.PackDescription,
    ScenarioPackCsvHeaders.PackAuthor,
    ScenarioPackCsvHeaders.PackAuthorLogoPath,
    ScenarioPackCsvHeaders.PackCategory,
    ScenarioPackCsvHeaders.PackIsSystem,
  ] as const;

  return rows
    .map((row, index) => {
      const errors = metadataHeaders
        .filter((header) => String(row[header] ?? '').trim() !== String(first[header] ?? '').trim())
        .map((header) => ({
          path: header,
          message: `${header} must match across all rows in the file.`,
        }));

      return errors.length ? { rowIndex: index + 1, errors } : null;
    })
    .filter(Boolean) as ValidationErrorItem[];
};

const createScenarioMapping = (row: ScenarioPackCsvRow, variables: Variable[]) => {
  const mappingErrors: ValidationErrorItem['errors'] = [];
  const allowedVariableIds = new Set(
    variables
      .map((variable) => variable.id)
      .filter((id): id is number => typeof id === 'number')
      .map((id) => String(id)),
  );

  const findUnknownVariableRefs = (formula: string) => {
    const matches = formula.match(/#(\d+)/g) ?? [];
    const unknown = Array.from(
      new Set(matches.filter((token) => !allowedVariableIds.has(token.replace('#', '')))),
    );
    return unknown;
  };

  const mappedSource = mapSourceToNumber(row[ScenarioCsvHeaders.Sources]);
  const mappedEvent = mapEventToNumber(row[ScenarioCsvHeaders.Events]);
  const mappedConsequence = mapConsequenceToNumber(row[ScenarioCsvHeaders.Consequences]);

  if (typeof mappedSource === 'string') {
    mappingErrors.push({ path: ScenarioCsvHeaders.Sources, message: mappedSource });
  }
  if (typeof mappedEvent === 'string') {
    mappingErrors.push({ path: ScenarioCsvHeaders.Events, message: mappedEvent });
  }
  if (typeof mappedConsequence === 'string') {
    mappingErrors.push({ path: ScenarioCsvHeaders.Consequences, message: mappedConsequence });
  }

  const rawLowerFormula = convertToString(row[ScenarioCsvHeaders.ConsequenceLowerFormula]);
  const rawUpperFormula = convertToString(row[ScenarioCsvHeaders.ConsequenceUpperFormula]);
  const rawLowerValue = rawLowerFormula || row[ScenarioCsvHeaders.UnmodifiedConsequenceLower];
  const rawUpperValue = rawUpperFormula || row[ScenarioCsvHeaders.UnmodifiedConsequenceUpper];

  const lowerResult = parseConsequenceValue(rawLowerValue, variables);
  const upperResult = parseConsequenceValue(rawUpperValue, variables);

  if (lowerResult.missingVariables.length) {
    mappingErrors.push({
      path: ScenarioCsvHeaders.ConsequenceLowerFormula,
      message: `Unknown variables: ${lowerResult.missingVariables.join(', ')}`,
    });
  }
  if (upperResult.missingVariables.length) {
    mappingErrors.push({
      path: ScenarioCsvHeaders.ConsequenceUpperFormula,
      message: `Unknown variables: ${upperResult.missingVariables.join(', ')}`,
    });
  }

  const unknownLowerRefs = findUnknownVariableRefs(lowerResult.formula);
  if (unknownLowerRefs.length) {
    mappingErrors.push({
      path: ScenarioCsvHeaders.ConsequenceLowerFormula,
      message: `Unknown variable references: ${unknownLowerRefs.join(', ')}. Use values from the exported template.`,
    });
  }

  const unknownUpperRefs = findUnknownVariableRefs(upperResult.formula);
  if (unknownUpperRefs.length) {
    mappingErrors.push({
      path: ScenarioCsvHeaders.ConsequenceUpperFormula,
      message: `Unknown variable references: ${unknownUpperRefs.join(', ')}. Use values from the exported template.`,
    });
  }

  const lowerNumericValue = convertToNumber(row[ScenarioCsvHeaders.UnmodifiedConsequenceLower]);
  const upperNumericValue = convertToNumber(row[ScenarioCsvHeaders.UnmodifiedConsequenceUpper]);
  const resolvedLowerFormula = lowerResult.formula ? lowerResult.formula.trim() : '';
  const resolvedUpperFormula = upperResult.formula ? upperResult.formula.trim() : '';

  const scenario: ScenarioPackImportScenarioInput = {
    id: convertToString(row[ScenarioCsvHeaders.ID]),
    name: convertToString(row[ScenarioCsvHeaders.Name]),
    description: convertToString(row[ScenarioPackCsvHeaders.ScenarioDescription]),
    status: convertToString(row[ScenarioCsvHeaders.Status]),
    source: typeof mappedSource === 'number' ? mappedSource : null,
    event: typeof mappedEvent === 'number' ? mappedEvent : null,
    consequence: typeof mappedConsequence === 'number' ? mappedConsequence : null,
    riskOwner: convertToString(row[ScenarioCsvHeaders.RiskOwner]),
    riskNarrative: convertToString(row[ScenarioCsvHeaders.RiskNarrative]),
    frequencyTimes: convertToNumber(row[ScenarioCsvHeaders.UnmodifiedFrequencyTimes]),
    frequencyYears: convertToNumber(row[ScenarioCsvHeaders.UnmodifiedFrequencyYears]),
    frequencyNote: convertToString(row[ScenarioCsvHeaders.FrequencyNote]),
    lower: lowerNumericValue,
    upper: upperNumericValue,
    lowerFormula:
      (rawLowerFormula ? resolvedLowerFormula || rawLowerFormula : null) ||
      (typeof row[ScenarioCsvHeaders.UnmodifiedConsequenceLower] === 'string' &&
      String(row[ScenarioCsvHeaders.UnmodifiedConsequenceLower]).includes('#')
        ? resolvedLowerFormula || String(row[ScenarioCsvHeaders.UnmodifiedConsequenceLower]).trim()
        : null),
    upperFormula:
      (rawUpperFormula ? resolvedUpperFormula || rawUpperFormula : null) ||
      (typeof row[ScenarioCsvHeaders.UnmodifiedConsequenceUpper] === 'string' &&
      String(row[ScenarioCsvHeaders.UnmodifiedConsequenceUpper]).includes('#')
        ? resolvedUpperFormula || String(row[ScenarioCsvHeaders.UnmodifiedConsequenceUpper]).trim()
        : null),
    consequenceNote: convertToString(row[ScenarioCsvHeaders.ConsequenceNote]),
    riskIdentifiedDate: convertDateToISO(row[ScenarioCsvHeaders.IdentifiedDate]),
    reviewDate: convertDateToISO(row[ScenarioCsvHeaders.ReviewDate]),
    managementStrategy: convertToString(row[ScenarioCsvHeaders.RiskManagementStrategy]),
    managementNote: convertToString(row[ScenarioCsvHeaders.RiskManagementNote]),
  };

  return { scenario, mappingErrors };
};

export const generateScenarioPackTemplateLink = () => {
  const csvString = scenarioPackHeaders.join(',') + '\n';
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  return URL.createObjectURL(blob);
};

export const parseScenarioPackCsv = async ({
  file,
  variables,
  existingScenarioIds,
  requirePackName = true,
}: {
  file: File;
  variables: Variable[];
  existingScenarioIds: string[];
  requirePackName?: boolean;
}): Promise<{
  payload: ScenarioPackImportPayload | null;
  validationErrors: ValidationErrorItem[];
  createdCount: number;
  updatedCount: number;
}> => {
  const tryParse = (delimiter: string) =>
    new Promise<ParseResult<ScenarioPackCsvRow>>((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        dynamicTyping: false,
        skipEmptyLines: true,
        delimiter,
        transformHeader: normalizeCsvHeader,
        complete: (parsed) =>
          resolve({
            ...parsed,
            data: (parsed.data || []).map((row) => normalizeCsvRowValues(row as ScenarioPackCsvRow)),
          }),
        error: reject,
      });
    });

  let result: ParseResult<ScenarioPackCsvRow> | null = null;
  let fallbackResult: ParseResult<ScenarioPackCsvRow> | null = null;
  for (const delimiter of delimiters) {
    const parsed = await tryParse(delimiter);
    const firstRow = (parsed.data?.[0] || {}) as ScenarioPackCsvRow;
    if (parsed.data && hasScenarioPackHeaders(firstRow)) {
      result = parsed;
      break;
    }

    if (!fallbackResult && parsed.data && Object.keys(firstRow).length > 2) {
      fallbackResult = parsed;
    }
  }

  if (!result) {
    result = fallbackResult;
  }

  if (!result || !result.data?.length) {
    return {
      payload: null,
      validationErrors: [{ rowIndex: 0, errors: [{ path: 'Data', message: 'No scenario pack data found.' }] }],
      createdCount: 0,
      updatedCount: 0,
    };
  }

  const missingHeaders = getMissingScenarioPackHeaders((result.data[0] || {}) as ScenarioPackCsvRow);
  if (missingHeaders.length) {
    return {
      payload: null,
      validationErrors: [
        {
          rowIndex: 0,
          errors: [
            {
              path: 'Headers',
              message:
                'This CSV is not in the Scenario Pack import format. Please upload a file exported from the Scenario Pack page.',
            },
            {
              path: 'Headers',
              message: `Missing required columns: ${missingHeaders.join(', ')}`,
            },
          ],
        },
      ],
      createdCount: 0,
      updatedCount: 0,
    };
  }

  if (result.data.length > 60) {
    return {
      payload: null,
      validationErrors: [
        {
          rowIndex: 0,
          errors: [{ path: 'Data', message: 'This file has more than 60 scenarios. Please upload up to 60 rows.' }],
        },
      ],
      createdCount: 0,
      updatedCount: 0,
    };
  }

  const validationSchema = buildScenarioPackValidationSchema(requirePackName);

  const rawValidationResults = await Promise.all(
    result.data.map((row, index) =>
      validateRow({
        row,
        rowIndex: index + 1,
        schema: validationSchema,
        pathNames: scenarioPackPathNames,
      }),
    ),
  );

  const validationErrors = rawValidationResults.filter((item) => item?.errors?.length) as ValidationErrorItem[];
  const metadataConsistencyErrors = createMetadataMismatchErrors(result.data);

  const mappedRows = result.data.map((row, index) => {
    const mapped = createScenarioMapping(row, variables);
    return mapped.mappingErrors.length
      ? ({
          rowIndex: index + 1,
          errors: mapped.mappingErrors,
        } satisfies ValidationErrorItem)
      : null;
  });

  const mappingErrors = mappedRows.filter(Boolean) as ValidationErrorItem[];
  const allErrors = [...validationErrors, ...metadataConsistencyErrors, ...mappingErrors];

  if (allErrors.length) {
    return {
      payload: null,
      validationErrors: allErrors,
      createdCount: 0,
      updatedCount: 0,
    };
  }

  const first = result.data[0];
  const scenarios = result.data
    .map((row) => createScenarioMapping(row, variables).scenario)
    .filter((scenario) => hasScenarioContent(scenario));

  const payload: ScenarioPackImportPayload = {
    group: {
      id: convertToString(first[ScenarioPackCsvHeaders.PackID]),
      name: convertToString(first[ScenarioPackCsvHeaders.PackName]) || '',
      description: convertToString(first[ScenarioPackCsvHeaders.PackDescription]),
      author: convertToString(first[ScenarioPackCsvHeaders.PackAuthor]),
      authorLogoPath: convertToString(first[ScenarioPackCsvHeaders.PackAuthorLogoPath]),
      category: convertToString(first[ScenarioPackCsvHeaders.PackCategory]),
      isSystem: normalizeBoolean(first[ScenarioPackCsvHeaders.PackIsSystem]),
    },
    scenarios,
  };

  const updatedCount = scenarios.filter((scenario) => scenario.id && existingScenarioIds.includes(scenario.id)).length;
  const createdCount = scenarios.filter((scenario) => !scenario.id || !existingScenarioIds.includes(scenario.id)).length;

  return {
    payload,
    validationErrors: [],
    createdCount,
    updatedCount,
  };
};
